/* eslint-disable no-empty-pattern */
import Vue from 'vue';
import { FBStorageRef } from '@/helpers/firebaseServices';
import { applyGuestFilters } from '@/utils/utilityFunctions';
import guestFilter from './guestFilter';
import guestExport from './guestExport';
import guestImport from './guestImport';

const timeoutDurationAddedGuest = 2000;
const timeoutDurationModifiedGuest = 2500;
const timeoutDurationRemovedGuest = 1000;

// firstName: '',
//   lastName: '',
//   email: '',
//   status: 1,
//   vip: false,
//   checkedInExtraGuests: 0,
//   totalExtraGuests: 0,
//   phoneNumber: '',
//   jobTitle: '',
//   organization: '',
//   notes: '',

// Guest object structure (Frontend)
// guest: {
//   firstName: '',
//   lastName: '',
//   email: '', (Optional)
//   status: 1, (Default: 1 - Attending)
//   vip: false,
//   totalExtraGuests: 0,
//   photoStoragePath: '',
//   # Other fields
//   checkedInExtraGuests: 0,
//   jobTitle: '',
//   phoneNumber: '',
//   organization: '',
//   notes: '',
//   badgeStoragePath: '',
//   badgeURL: '',
//   barcodeValue: '',
//   GENERATED
//   photoURL: '',
// },

function normalizeGuestData(guestData) {
  return {
    ...guestData.firstName !== undefined && { firstName: `${guestData.firstName}` },
    ...guestData.lastName !== undefined && { lastName: `${guestData.lastName}` },
    ...guestData.photoStoragePath && { photoStoragePath: `${guestData.photoStoragePath}` },
    ...guestData.badgeStoragePath && { badgeStoragePath: `${guestData.badgeStoragePath}` },
    ...guestData.email && { email: `${guestData.email}` },
    ...guestData.status !== undefined && { status: parseInt(guestData.status, 10) },
    ...guestData.vip !== undefined && { vip: !!guestData.vip },
    ...guestData.totalExtraGuests !== undefined && { totalExtraGuests: parseInt(guestData.totalExtraGuests, 10) },
    ...guestData.phone && { phoneNumber: `${guestData.phone}` },
    ...guestData.jobPosition && { jobTitle: `${guestData.jobPosition}` },
    ...guestData.company && { organization: `${guestData.company}` },
    ...guestData.notes && { note: `${guestData.notes}` },
    // eslint-disable-next-line max-len
    ...guestData.checkedInExtraGuests !== undefined && { checkedInExtraGuests: parseInt(guestData.checkedInExtraGuests, 10) },
    ...guestData.barcodeValue !== undefined && { barcodeValue: guestData.barcodeValue },
    ...guestData.creationTime && typeof guestData.creationTime.toDate === 'function' && { creationTime: guestData.creationTime.toDate() },
    ...guestData.modificationTime && typeof guestData.modificationTime.toDate === 'function' && { modificationTime: guestData.modificationTime.toDate() },
    ...guestData.attendingTime && typeof guestData.attendingTime.toDate === 'function' && { attendingTime: guestData.attendingTime.toDate() },
    ...guestData.checkInTime && typeof guestData.checkInTime.toDate === 'function' && { checkInTime: guestData.checkInTime.toDate() },
    ...guestData.checkOutTime && typeof guestData.checkOutTime.toDate === 'function' && { checkOutTime: guestData.checkOutTime.toDate() },
    ...guestData.createdBy && { createdBy: `${guestData.createdBy}` },
  };
}

export default {
  namespaced: true,
  modules: {
    filter: guestFilter,
    export: guestExport,
    import: guestImport,
  },
  state: {
    isInitialLoading: true,
    isLoadingSnapshot: true,
    isDeletingGuests: false,
    deletingGuestsTimerIsRunning: false,
    guests: [],
    guestsCounts: {
      mainGuest: 0,
      extraGuest: 0,
    },
  },
  getters: {
    guestsIsEmpty: (state) => state.guests.length === 0,
  },
  mutations: {
    RESET_GUEST_STATE(state) {
      state.isInitialLoading = true;
      state.isLoadingSnapshot = true;
      state.isDeletingGuests = false;
      state.guests = [];
      state.guestsCounts.mainGuest = 0;
      state.guestsCounts.extraGuest = 0;
    },
    SET_IS_LOADING_SNAPSHOT(state, isLoadingSnapshot) {
      state.isLoadingSnapshot = isLoadingSnapshot;
    },
    SET_IS_INITIAL_LOADING(state, isInitialLoading) {
      state.isInitialLoading = isInitialLoading;
    },
    SET_IS_DELETING_GUESTS(state, isDeletingGuests) {
      state.isDeletingGuests = isDeletingGuests;
    },
    SET_DELETING_GUESTS_TIMER_IS_RUNNING(state, deletingGuestsTimerIsRunning) {
      state.deletingGuestsTimerIsRunning = deletingGuestsTimerIsRunning;
    },
    ADD_GUESTS(state, { guestsToAdd, initialGuestsIndexLoading }) {
      if (initialGuestsIndexLoading) {
        state.guests = guestsToAdd;
        return;
      }

      guestsToAdd.forEach((guestData) => {
        state.guests.splice(guestData.guestIndex, 0, guestData.guestData);
      });
    },
    MOVE_GUEST(state, { oldGuestIndex, newGuestIndex }) {
      state.guests.splice(newGuestIndex, 0, state.guests.splice(oldGuestIndex, 1)[0]); // Move index in array
    },
    REMOVE_GUESTS(state, guestIndexes) {
      guestIndexes.forEach((guestIndex) => {
        state.guests.splice(guestIndex, 1);
      });
    },
    SET_SNAPSHOT_CHANGE_FOR_GUESTS(state, { guestIndexes, snapshotChange }) {
      guestIndexes.forEach((guestIndex) => {
        if (state.guests[guestIndex]) {
          state.guests[guestIndex].snapshotChange = snapshotChange;
        }
      });
    },
    RESET_SNAPSHOT_CHANGE_FOR_GUESTS(state, guestIndexes) {
      guestIndexes.forEach((guestIndex) => {
        if (state.guests[guestIndex]) {
          state.guests[guestIndex].snapshotChange = '';
        }
      });
    },
    SET_GUEST_PROPERTIES(state, { guestIndex, guestData }) {
      if (state.guests[guestIndex] === undefined) return;

      Vue.set(state.guests, guestIndex, guestData);
    },
    SET_GUESTS_COUNTS(state, { mainGuest, extraGuest }) {
      state.guestsCounts.mainGuest += mainGuest;
      state.guestsCounts.extraGuest += extraGuest;
    },
  },
  actions: {
    resetGuestState: ({ commit }) => {
      commit('RESET_GUEST_STATE');
    },
    updateIsInitialLoading: ({ commit }, isInitialLoading) => {
      commit('SET_IS_INITIAL_LOADING', isInitialLoading);
      return Promise.resolve();
    },
    updateIsDeletingGuests: ({ commit }, isDeletingGuests) => {
      commit('SET_IS_DELETING_GUESTS', isDeletingGuests);
      return Promise.resolve();
    },
    onGuestsSnapshotChange: ({
      state, rootState, commit, dispatch,
    }, { snapshot, firstNameFirstOrder }) => {
      commit('SET_IS_LOADING_SNAPSHOT', true);

      const filterCountModifiers = [];
      const guestsToAdd = [];
      const guestIndexesToAdd = [];
      const guestIndexesToRemove = [];
      const initialGuestsIndexLoading = state.isInitialLoading;

      // const guestListGuestIndexesToRemove = [];
      let mainGuestCount = 0;
      let extraGuestCount = 0;
      let filterCountModifier = null;
      for (let i = 0; i < rootState.guest.filter.filterCounts.length; i += 1) {
        filterCountModifiers.push({
          mainGuest: 0,
          extraGuest: 0,
        });
      }

      snapshot.docChanges().forEach((change) => {
        const guestData = normalizeGuestData(change.doc.data());

        // Special handling for first snapshot load or import
        if (initialGuestsIndexLoading) {
          guestsToAdd.push({
            id: change.doc.id,
            snapshotChange: '',
            photoURL: '',
            listIndex: change.newIndex,
            fullName: firstNameFirstOrder
              ? `${guestData.firstName} ${guestData.lastName}` : `${guestData.lastName} ${guestData.firstName}`,
            ...guestData,
          });

          // Updating global guest counts
          mainGuestCount += 1;
          extraGuestCount += guestData.totalExtraGuests || 0;

          filterCountModifier = applyGuestFilters(
            state.filter.filters,
            rootState.guest.filter.filterConditions,
            { newGuestData: guestData },
          );
        } else if (change.type === 'added') {
          // Adding guest to guests list
          guestsToAdd.push({
            guestIndex: change.newIndex,
            guestData: {
              id: change.doc.id,
              snapshotChange: rootState.guest.import.isImportingGuests ? '' : 'added',
              listIndex: change.newIndex,
              photoURL: '',
              fullName: firstNameFirstOrder
                ? `${guestData.firstName} ${guestData.lastName}` : `${guestData.lastName} ${guestData.firstName}`,
              ...guestData,
            },
          });
          guestIndexesToAdd.push(change.newIndex);

          // Updating global guest counts
          mainGuestCount += 1;
          extraGuestCount += guestData.totalExtraGuests || 0;

          filterCountModifier = applyGuestFilters(
            state.filter.filters,
            rootState.guest.filter.filterConditions,
            { newGuestData: guestData },
          );
        } else if (change.type === 'modified') {
          filterCountModifier = applyGuestFilters(
            state.filter.filters,
            rootState.guest.filter.filterConditions,
            { newGuestData: guestData, oldGuestData: state.guests[change.oldIndex] },
          );

          // Update global guest counts
          let totalExtraGuestsBefore = 0;
          if (state.guests[change.oldIndex] !== undefined && state.guests[change.oldIndex].totalExtraGuests) {
            totalExtraGuestsBefore = state.guests[change.oldIndex].totalExtraGuests;
          }
          const totalExtraGuestsAfter = guestData.totalExtraGuests || 0;
          const totalExtraGuestModifier = totalExtraGuestsAfter - totalExtraGuestsBefore;
          const snapshotChange = 'modified';

          if (totalExtraGuestModifier !== 0) { // Update extra's if count changed
            extraGuestCount += totalExtraGuestModifier;
          }

          // Updating guest order
          if (change.oldIndex !== change.newIndex) {
            commit('MOVE_GUEST', {
              oldGuestIndex: change.oldIndex,
              newGuestIndex: change.newIndex,
            });
          }

          const oldGuestPhotoStoragePath = state.guests[change.newIndex].photoStoragePath;

          // Updating guest data at new index
          commit('SET_GUEST_PROPERTIES', {
            guestIndex: change.newIndex,
            guestData: {
              id: change.doc.id,
              snapshotChange,
              listIndex: change.newIndex,
              // Add photoURL field if it was present
              photoURL: !guestData.photoStoragePath && oldGuestPhotoStoragePath ? '' : state.guests[change.newIndex].photoURL,
              fullName: firstNameFirstOrder
                ? `${guestData.firstName} ${guestData.lastName}` : `${guestData.lastName} ${guestData.firstName}`,
              ...guestData,
            },
          });

          // Get photoURL if it changed (photoStoragePath field was added or updated)
          if (guestData.photoStoragePath && (!oldGuestPhotoStoragePath
            || (oldGuestPhotoStoragePath && guestData.photoStoragePath !== oldGuestPhotoStoragePath))) {
            dispatch('updateGuestPhotoURL', change.newIndex);
          }

          // Showing update with snapshot change
          setTimeout(() => {
            dispatch('resetGuestSnapshotChange', [change.newIndex]);
          }, timeoutDurationModifiedGuest);
        } else if (change.type === 'removed') {
          guestIndexesToRemove.push(change.oldIndex);

          // Update global guest counts
          mainGuestCount += -1;
          extraGuestCount += -change.doc.data().totalExtraGuests || 0;

          filterCountModifier = applyGuestFilters(
            state.filter.filters,
            rootState.guest.filter.filterConditions,
            { oldGuestData: change.doc.data() },
          );
        }

        // Apply filterCountModifiers
        filterCountModifier.forEach((filterCounts, filterIndex) => {
          filterCountModifiers[filterIndex].mainGuest += filterCounts.mainGuest;
          filterCountModifiers[filterIndex].extraGuest += filterCounts.extraGuest;
        });
      });

      if (guestsToAdd.length) {
        commit('ADD_GUESTS', { guestsToAdd, initialGuestsIndexLoading });

        if (!initialGuestsIndexLoading) {
          setTimeout(() => {
            dispatch('resetGuestSnapshotChange', guestIndexesToAdd);
          }, timeoutDurationAddedGuest);
        }
      }

      dispatch('updateGuestCountsAndFilters', {
        mainGuestCount,
        extraGuestCount,
        filterCountModifiers,
      });

      if (guestIndexesToRemove.length) {
        if (!state.isInitialLoading && !rootState.guest.import.isImportingGuests) {
          const guestListGuestIndexesToRemove = [];
          let deletedCount = 0;

          guestIndexesToRemove.forEach((guestIndex) => {
            guestListGuestIndexesToRemove.push(guestIndex + deletedCount);
            deletedCount += 1;
          });

          commit('SET_SNAPSHOT_CHANGE_FOR_GUESTS', {
            guestIndexes: guestListGuestIndexesToRemove,
            snapshotChange: 'removed',
          });
          commit('SET_DELETING_GUESTS_TIMER_IS_RUNNING', true);
          setTimeout(() => {
            commit('REMOVE_GUESTS', guestIndexesToRemove);
            commit('SET_DELETING_GUESTS_TIMER_IS_RUNNING', false);
          }, timeoutDurationRemovedGuest);
        } else {
          commit('REMOVE_GUESTS', guestIndexesToRemove);
        }
      }

      commit('SET_IS_LOADING_SNAPSHOT', false);

      return Promise.resolve();
    },
    updateGuestCountsAndFilters: ({ state, commit, dispatch },
      { mainGuestCount, extraGuestCount, filterCountModifiers }) => {
      // Updating global guest counts
      commit('SET_GUESTS_COUNTS', {
        mainGuest: mainGuestCount,
        extraGuest: extraGuestCount,
      });

      // Update filter guest counts
      dispatch('filter/updateFilterCounts', filterCountModifiers);

      // Re-filter guests
      if (!state.isInitialLoading && !state.isDeletingGuests) { // TODO: Check if this is needed
        dispatch('filter/updateFilteredGuests');
      }
    },
    updateGuestPhotoURL: ({ state, commit }, guestIndex) => {
      if (!state.guests[guestIndex] || !state.guests[guestIndex].photoStoragePath) return;

      FBStorageRef.child(state.guests[guestIndex].photoStoragePath)
        .getDownloadURL()
        .then((photoURL) => {
          commit('SET_GUEST_PROPERTIES', {
            guestIndex,
            guestData: {
              ...state.guests[guestIndex],
              photoURL,
            },
          });
        })
        .catch((error) => { // Handle errors
          switch (error.code) {
            case 'storage/object_not_found':
              console.warn('Guest photo was not found');
              break;
            case 'storage/unauthorized':
              console.warn('User doesn\'t have permission to access the object');
              break;
            case 'storage/unknown':
              console.warn('Unknown error occured');
              break;
            default:
              console.warn('Unknown error occured');
          }
        });
    },
    resetGuestSnapshotChange: ({ commit }, guestIndexes) => {
      commit('RESET_SNAPSHOT_CHANGE_FOR_GUESTS', guestIndexes);
    },
  },
};
